import type { ReactNode } from 'react';
import type { Dish } from '../types';
import { colors, primaryButtonStyle } from '../lib/theme';

interface Props {
  dish: Dish;
  onSelect?: () => void;
  actionLabel?: string;
  actions?: ReactNode;
  /** 'card' (default) is a standalone pastel box, e.g. in the Library list.
   *  'flat' has no background of its own, for use inside an already-colored
   *  container (e.g. a meal-slot section) so the fill isn't duplicated per item. */
  variant?: 'card' | 'flat';
}

export default function DishCard({ dish, onSelect, actionLabel = 'Choose', actions, variant = 'card' }: Props) {
  const isCard = variant === 'card';
  return (
    <div
      style={{
        background: isCard ? colors.blue : 'transparent',
        color: isCard ? colors.blueText : 'inherit',
        borderRadius: isCard ? 14 : 0,
        padding: isCard ? '0.75rem 1rem' : '0.4rem 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
      }}
    >
      {dish.photoUrl && (
        <img
          src={dish.photoUrl}
          alt=""
          style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700 }}>
          {dish.isFavorite && '★ '}
          {dish.name}
        </div>
        <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>
          {dish.calories} kcal · {dish.proteinGrams}g protein
          {dish.prepTimeMinutes != null ? ` · ${dish.prepTimeMinutes} min` : ''}
        </div>
        {dish.products.length > 0 && (
          <details style={{ marginTop: '0.3rem' }} onClick={(e) => e.stopPropagation()}>
            <summary style={{ fontSize: '0.8rem', opacity: 0.75, cursor: 'pointer' }}>
              Products ({dish.products.length})
            </summary>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
              {dish.products.map((product) => (
                <span
                  key={product.id}
                  style={{
                    background: 'rgba(0,0,0,0.08)',
                    borderRadius: 999,
                    padding: '0.15rem 0.55rem',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {product.name}
                  {product.amountGrams != null ? ` ${product.amountGrams}g` : ''}
                  {product.calories != null ? ` ${product.calories} kcal` : ''}
                </span>
              ))}
            </div>
          </details>
        )}
      </div>
      {(onSelect || actions) && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
          {onSelect && (
            <button onClick={onSelect} style={{ ...primaryButtonStyle, whiteSpace: 'nowrap' }}>
              {actionLabel}
            </button>
          )}
          {actions}
        </div>
      )}
    </div>
  );
}
